<template>
  <BaseDataTable
    :entries="entries"
    :columns="columns"
    :loading="loading"
    :paginator-info="paginatorInfo"
    :default-page-length="defaultPageLength"
    :multi-select="multiSelect"
    :pre-selected-rows="preSelectedRows"
    class="trusted-contact-table"
    data-test="trusted-contact-table"
    row-hover
    tall-rows
    max-height="90%"
    preserve-selected-rows
    row-uuid="id"
    @search="$emit('search', $event)"
    @row-click="$emit('row-click', $event)"
    @rows-selected="$emit('rows-selected', $event)"
  >
    <template #Head-Skills>
      <div class="tw-flex tw-flex-wrap tw-gap-sm lg:tw-max-w-[200px] xl:tw-max-w-none">
        <WdsLabel primary subtle sm>
          {{ $t('Skills added by company') }}
        </WdsLabel>
        <WdsLabel subtle sm :disabled="!props.showWorkerSkills">
          <WdsStack align-center>
            <WdsIcon
              :name="props.showWorkerSkills ? 'view' : 'view-off'"
              :size="14"
              class="tw-me-1"
            />
            {{ $t('Added by worker') }}
          </WdsStack>
        </WdsLabel>
      </div>
    </template>
    <template v-for="name in Object.keys($slots).filter(filterSlots)" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps || {}"></slot>
    </template>
    <template #Freelancer="{ row, rowIndex }">
      <ContactNameColumn
        :contact="row"
        :index="rowIndex"
        :with-hire-status="!props.forOrganisation"
      />
    </template>
    <template #Skills="{ row, rowIndex }">
      <LabelGroup
        :visible-tags="
          getVisibleSkills(props.showWorkerSkills ? row.combinedSkills : row.companySkills)
        "
        :additional-tags="
          getAdditionalSkills(props.showWorkerSkills ? row.combinedSkills : row.companySkills)
        "
        :data-test="`tc-skills-${rowIndex}`"
        sm
        gray
        subtle
        tooltip-is-global
      >
        <template #tooltip-header>
          <WdsIcon name="tag" :size="16" />
          {{ $t('Skills') }}
        </template>
      </LabelGroup>
    </template>
    <template #Verified="{ row, rowIndex }">
      <WdsStack
        direction-row
        justify-center
        gap-sm
        align-center
        :data-test="`tc-verification-${rowIndex}`"
      >
        <WdsTooltip v-if="row.verified">
          <template #tooltip-content>
            {{ $t('Right To Work') }}
            <br />
            {{ $t('Completed:') }}
            {{ row.rtwVerifiedAt === null ? 'unknown date' : row.rtwVerifiedAt }}
            <br />
            {{ $t('Expires:') }} {{ row.rtwExpiresAt === null ? 'unknown date' : row.rtwExpiresAt }}
          </template>
          <WdsIcon
            :size="18"
            :name="WdsIconNameEnum.SHIELD_SUCCESS"
            class="tw-flex-shrink-0"
            color="#057D43"
          />
        </WdsTooltip>
      </WdsStack>
    </template>
    <template #Jobs="{ row, rowIndex }">
      <div :data-test="`tc-job-count-${rowIndex}`">{{ row.jobsCount }}</div>
    </template>
    <template #Location="{ row, rowIndex }">
      <ContactCountryColumn :contact="row" :index="rowIndex" />
    </template>
    <template #BusinessSetup="{ row, rowIndex }">
      <div :data-test="`tc-business-${rowIndex}`">{{ row.businessSetup ?? '-' }}</div>
    </template>
    <template #Notes="{ row }">
      <WdsButtonGhost @click="openDetailsForTab(row, 'trusted-contact-notes')">
        <WdsIcon v-if="row.notesCount > 0" :name="WdsIconNameEnum.DOCUMENT_ALT" :size="16" />
      </WdsButtonGhost>
    </template>
    <template #no-results="{ loading }">
      <WdsStack direction-row justify-center>
        <BaseLoadingNotice v-if="loading" class="tw-z-10" data-test="tc-loading">
          <template #main>{{ $t('Finding matching talent') }}</template>
        </BaseLoadingNotice>
        <template v-else-if="!loading && !isError">
          <slot name="no-trusted-contacts-found">
            {{ $t('No matching records found') }}
          </slot>
        </template>
      </WdsStack>
    </template>
  </BaseDataTable>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsStack,
  WdsTooltip,
  WdsLabel,
  WdsIconNameEnum,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import BaseDataTable from '@/components/BaseDataTable'
import LabelGroup from '@/components/LabelGroup.vue'
import { graphqlFragmentValidator } from '@/utils/propValidation'
import { PAGINATOR_INFO_FRAGMENT } from '@/components/ProvidePagination.vue'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import { AccountType } from '@/gql/graphql'
import ContactNameColumn from '@/components/Contacts/CompanyFreelancer/ContactColumns/ContactNameColumn.vue'
import ContactCountryColumn from '@/components/Contacts/CompanyFreelancer/ContactColumns/ContactCountryColumn.vue'

const emit = defineEmits(['rows-selected', 'row-click', 'search'])

const props = defineProps({
  entries: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    required: true,
  },
  paginatorInfo: {
    type: Object,
    default: undefined,
    validator: (value) => graphqlFragmentValidator(PAGINATOR_INFO_FRAGMENT)(value),
  },
  additionalColumns: {
    type: Array,
    default: [],
  },
  multiSelect: {
    type: Boolean,
    default: false,
  },
  preSelectedRows: {
    type: Array,
    default: [],
  },
  maxSkills: {
    type: Number,
    default: 3,
  },
  defaultPageLength: {
    type: Number,
    default: 50,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  forOrganisation: {
    type: Boolean,
    default: false,
  },
  showWorkerSkills: {
    type: Boolean,
    default: false,
  },
})

const standardColumns = [
  {
    key: 'freelancer',
    slot: 'Freelancer',
    title: $t('Name'),
    order: 'asc',
    class: 'tw-cursor-pointer',
  },
  // {
  //   key: 'verified',
  //   slot: 'Verified',
  //   title: $t('Verified'),
  //   class: 'tw-cursor-pointer',
  //   orderAble: true,
  // },
  {
    key: 'skills',
    slot: 'Skills',
    title: $t('Skills'),
    class: 'vmiddle tw-cursor-pointer',
  },
  {
    key: 'jobsCount',
    slot: 'Jobs',
    title: $t('Jobs taken'),
    class: 'vmiddle text-center',
    colClass: { 'tw-whitespace-nowrap': true },
  },
  {
    key: 'countryColumn',
    slot: 'Location',
    title: $t('Location'),
    class: 'vmiddle tw-cursor-pointer tw-text-gray-850 tw-text-[13px]',
  },
  {
    key: 'businessSetup',
    slot: 'BusinessSetup',
    title: $t('Business setup'),
    class: 'vmiddle tw-cursor-pointer',
    colClass: { 'tw-whitespace-nowrap': true },
  },
  {
    key: 'notes',
    slot: 'Notes',
    title: $t('Notes'),
    class: 'vmiddle',
    colClass: { 'tw-whitespace-nowrap': true },
    ignoreClickForRowSelection: true,
  },
]

const columns = computed(() => [...standardColumns, ...props.additionalColumns])

const getVisibleSkills = (skills) =>
  skills
    ?.slice(0, props.maxSkills)
    ?.map((skill) => ({ name: skill.name, primary: skill.skillType === AccountType.Company }))

const getAdditionalSkills = (skills) =>
  skills?.slice(props.maxSkills, skills.length)?.map((skill) => skill.name)

const filterSlots = (slotName: string) => !standardColumns.map((col) => col.slot).includes(slotName)

const openDetailsForTab = (row, tabName: string) => {
  // eslint-disable-next-line no-param-reassign
  row.tabName = tabName

  emit('row-click', row)
}
</script>

<style scoped lang="scss"></style>
